/**
 * Allowed Emails Admin Page
 *
 * Allows the dashboard admin to manage email whitelist and per-user group permissions.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../../helpers/apiUtils';

const GROUP_OPTIONS: { key: string; label: string }[] = [
  { key: 'overview', label: 'Übersicht' },
  { key: 'leads_doi', label: 'Leads & DOI' },
  { key: 'adserver', label: 'AdServer' },
  { key: 'email_marketing', label: 'E-Mail Marketing' },
  { key: 'revenue_vnr', label: 'Umsatz & VNR' },
  { key: 'fit_products', label: 'FIT Produkte' },
  { key: 'webinars_mailings', label: 'Webinare & Mailings' },
  { key: 'content_partners', label: 'Content & Partner' },
  { key: 'administration', label: 'Administration' },
  // Bis 2026-10-12 fehlten hier vier Gruppen, die der Server laengst kennt (groupAuth.ts VALID_GROUPS) —
  // sie liessen sich nur per DB vergeben.
  { key: 'nfa_external', label: 'NFA Extern' },
  { key: 'ga4_traffic', label: 'Portal Traffic (GA4)' },
  { key: 'live', label: 'Live Streaming' },
  { key: 'broker_hub', label: 'Broker Hub' },
  { key: 'msm_partner', label: 'MSM Partner (sieht nur das MSM-Dashboard)' },
];

interface AllowedEmail {
  id: number;
  email: string;
  added_by: string;
  added_at: string;
  notes?: string;
  allowed_groups: string[] | null;
}

const AllowedEmails: React.FC = () => {
  const [emails, setEmails] = useState<AllowedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add email form state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<AllowedEmail | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Group edit state
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupEditEmail, setGroupEditEmail] = useState<AllowedEmail | null>(null);
  const [groupEditFullAccess, setGroupEditFullAccess] = useState(true);
  const [groupEditSelected, setGroupEditSelected] = useState<string[]>([]);
  const [groupSaveLoading, setGroupSaveLoading] = useState(false);

  // Load allowed emails on component mount
  useEffect(() => {
    loadAllowedEmails();
  }, []);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadAllowedEmails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/admin/allowed-emails');
      setEmails(response.data.data.emails);
    } catch (err: any) {
      console.error('Error loading allowed emails:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load allowed emails');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async () => {
    try {
      setAddLoading(true);
      setError(null);

      if (!newEmail.trim()) {
        setError('Bitte geben Sie eine E-Mail-Adresse ein');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        return;
      }

      await api.post('/api/admin/allowed-emails', {
        email: newEmail.trim(),
        notes: newNotes.trim() || undefined,
      });

      setSuccess(`E-Mail-Adresse ${newEmail} wurde erfolgreich hinzugefügt`);
      setNewEmail('');
      setNewNotes('');
      setAddDialogOpen(false);
      loadAllowedEmails();
    } catch (err: any) {
      console.error('Error adding email:', err);
      setError(err.response?.data?.error || err.message || 'Failed to add email');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteClick = (email: AllowedEmail) => {
    setEmailToDelete(email);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!emailToDelete) return;

    try {
      setDeleteLoading(true);
      setError(null);

      await api.delete(`/api/admin/allowed-emails/${emailToDelete.id}`);

      setSuccess(`E-Mail-Adresse ${emailToDelete.email} wurde erfolgreich entfernt`);
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
      loadAllowedEmails();
    } catch (err: any) {
      console.error('Error deleting email:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete email');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEmailToDelete(null);
  };

  // Group management
  const handleGroupEditClick = (email: AllowedEmail) => {
    setGroupEditEmail(email);
    const hasFullAccess = email.allowed_groups === null;
    setGroupEditFullAccess(hasFullAccess);
    setGroupEditSelected(hasFullAccess ? [] : email.allowed_groups || []);
    setGroupDialogOpen(true);
  };

  const handleGroupToggle = (groupKey: string) => {
    setGroupEditSelected((prev) =>
      prev.includes(groupKey) ? prev.filter((g) => g !== groupKey) : [...prev, groupKey],
    );
  };

  const handleGroupSave = async () => {
    if (!groupEditEmail) return;

    try {
      setGroupSaveLoading(true);
      setError(null);

      const allowedGroups = groupEditFullAccess ? null : groupEditSelected;

      await api.put(`/api/admin/allowed-emails/${groupEditEmail.id}/groups`, {
        allowed_groups: allowedGroups,
      });

      setSuccess(`Berechtigungen für ${groupEditEmail.email} wurden aktualisiert`);
      setGroupDialogOpen(false);
      setGroupEditEmail(null);
      loadAllowedEmails();
    } catch (err: any) {
      console.error('Error updating groups:', err);
      setError(err.response?.data?.error || err.message || 'Fehler beim Aktualisieren');
    } finally {
      setGroupSaveLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getGroupLabel = (key: string): string => {
    return GROUP_OPTIONS.find((g) => g.key === key)?.label || key;
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Berechtigungen verwalten
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          E-Mail hinzufügen
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>E-Mail-Adresse</TableCell>
                <TableCell>Bereiche</TableCell>
                <TableCell>Hinzugefügt von</TableCell>
                <TableCell>Hinzugefügt am</TableCell>
                <TableCell>Notizen</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Keine berechtigten E-Mail-Adressen gefunden
                  </TableCell>
                </TableRow>
              ) : (
                emails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell>{email.email}</TableCell>
                    <TableCell>
                      {email.allowed_groups === null ? (
                        <Chip label="Alle" size="small" color="success" variant="outlined" />
                      ) : email.allowed_groups.length === 0 ? (
                        <Chip label="Keine" size="small" color="error" variant="outlined" />
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {email.allowed_groups.map((g) => (
                            <Chip
                              key={g}
                              label={getGroupLabel(g)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11 }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{email.added_by}</TableCell>
                    <TableCell>{formatDate(email.added_at)}</TableCell>
                    <TableCell>{email.notes || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleGroupEditClick(email)}
                        title="Bereiche bearbeiten"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(email)}
                        title="Entfernen"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Email Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>E-Mail-Adresse hinzufügen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="E-Mail-Adresse"
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notizen (optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleAddEmail}
            variant="contained"
            color="primary"
            disabled={addLoading}
          >
            {addLoading ? <CircularProgress size={24} /> : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>E-Mail-Adresse entfernen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die E-Mail-Adresse <strong>{emailToDelete?.email}</strong> wirklich aus der
            Berechtigungsliste entfernen?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Diese Person kann danach nicht mehr auf das Dashboard zugreifen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Entfernen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Edit Dialog */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bereiche bearbeiten</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {groupEditEmail?.email}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={groupEditFullAccess}
                onChange={(e) => setGroupEditFullAccess(e.target.checked)}
              />
            }
            label="Vollzugriff (alle Bereiche)"
            sx={{ mb: 2, display: 'block' }}
          />

          {!groupEditFullAccess && (
            <FormGroup>
              {GROUP_OPTIONS.map((group) => (
                <FormControlLabel
                  key={group.key}
                  control={
                    <Checkbox
                      checked={groupEditSelected.includes(group.key)}
                      onChange={() => handleGroupToggle(group.key)}
                    />
                  }
                  label={group.label}
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)} disabled={groupSaveLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleGroupSave}
            variant="contained"
            color="primary"
            disabled={groupSaveLoading}
          >
            {groupSaveLoading ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllowedEmails;
